import { Link } from "react-router-dom";
import {
  SparklesIcon,
  UserGroupIcon,
  HomeModernIcon,
  ShieldCheckIcon,
  AdjustmentsHorizontalIcon,
  CalendarDaysIcon,
  ArrowsRightLeftIcon,
  CommandLineIcon,
  ClipboardDocumentListIcon,
  ScaleIcon,
  ChartBarIcon,
  UsersIcon,
} from "@heroicons/react/24/outline";
import PageHeader from "../../components/layout/PageHeader";
import { Card } from "../../components/ui/Card";
import { useAuth } from "../../hooks/useAuth";

const adminSections = [
  {
    title: "Manage Activities",
    desc: "Add, edit, and organize activities and achievement credit definitions.",
    icon: SparklesIcon,
    to: "/admin/activities",
    color: "bg-forest-100 text-forest-700",
  },
  {
    title: "Manage Staff",
    desc: "Create counselor accounts and assign them to cabin groups.",
    icon: UserGroupIcon,
    to: "/admin/staff",
    color: "bg-amber-100 text-amber-700",
  },
  {
    title: "Manage Cabins & Divisions",
    desc: "Set up cabin groups and divisions for the current session.",
    icon: HomeModernIcon,
    to: "/admin/cabins",
    color: "bg-bark-100 text-bark-700",
  },
  {
    title: "Manage Terms",
    desc: "Define camp session terms with start and end dates for each summer.",
    icon: CalendarDaysIcon,
    to: "/admin/terms",
    color: "bg-teal-100 text-teal-700",
  },
  {
    title: "Display Name Configuration",
    desc: "Customise terminology — rename Camper, AC, Kerchief, and more for your camp.",
    icon: AdjustmentsHorizontalIcon,
    to: "/admin/config",
    color: "bg-stone-100 text-stone-700",
  },
  {
    title: "Import & Export",
    desc: "Bulk import or export ACs, activities, campers, staff, and cabins via CSV.",
    icon: ArrowsRightLeftIcon,
    to: "/admin/imports",
    color: "bg-indigo-100 text-indigo-700",
  },
  {
    title: "Manage ACs",
    desc: "Enter ACs from paper books or bulk-enter historical ACs for a single camper.",
    icon: ClipboardDocumentListIcon,
    to: "/admin/manage-acs",
    color: "bg-sky-100 text-sky-700",
  },
  {
    title: "Manage Campers",
    desc: "Search campers across all years and merge duplicates to consolidate ACs.",
    icon: UsersIcon,
    to: "/admin/manage-campers",
    color: "bg-rose-100 text-rose-700",
  },
  {
    title: "Team Assignment",
    desc: "Assign Army or Navy teams to campers and staff, balancing divisions.",
    icon: ScaleIcon,
    to: "/admin/team-assignment",
    color: "bg-army-100 text-army-700",
  },
  {
    title: "Trend Reporting",
    desc: "Enrollment and AC trends over time — by term, cabin, and metric.",
    icon: ChartBarIcon,
    to: "/admin/trends",
    color: "bg-forest-100 text-forest-700",
  },
];

export default function AdminDashboard() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  return (
    <div>
      <PageHeader
        title="Admin"
      />

      <div className="p-6 lg:p-8">
        <div className="flex items-center gap-2 mb-6 text-sm text-stone-500">
          <ShieldCheckIcon className="w-4 h-4 text-forest-500" />
          Admin &amp; Director access only
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {adminSections.map((section) => {
            const Icon = section.icon;
            return (
              <Link key={section.to} to={section.to} className="camp-card p-6 block group">
                <div
                  className={`w-11 h-11 rounded-xl flex items-center justify-center mb-4 ${section.color}`}
                >
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="font-semibold text-stone-900 group-hover:text-forest-700 transition-colors">
                  {section.title}
                </h3>
                <p className="text-sm text-stone-500 mt-1">{section.desc}</p>
              </Link>
            );
          })}
        </div>

        {isAdmin && (
          <div className="mt-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-px flex-1 bg-stone-200" />
              <span className="text-xs font-medium text-stone-400 uppercase tracking-wide">Developer</span>
              <div className="h-px flex-1 bg-stone-200" />
            </div>
            <Link
              to="/admin/developer-tools"
              className="camp-card p-6 block group border border-amber-200 bg-amber-50 hover:bg-amber-100 max-w-xs"
            >
              <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4 bg-amber-100 text-amber-700">
                <CommandLineIcon className="w-5 h-5" />
              </div>
              <h3 className="font-semibold text-stone-900 group-hover:text-amber-700 transition-colors">
                Developer Tools
              </h3>
              <p className="text-sm text-stone-500 mt-1">Debug mode and diagnostic settings.</p>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
